//dependencies
import type { AxiosError } from "axios";
import React, { useEffect } from "react";
import { To, useLocation, useNavigate, useParams } from "react-router-dom";

import { Breadcrumbs } from "@performyard/py-components";

// components
import Button from "../../../components/Button";
import ContentModal from "../../../components/Modal/Templates/ContentModal";
import Spacer from "../../../components/Spacer";
import ComponentLoader from "../../../components/Spinner/ComponentLoader";
import Typography from "../../../components/Text";
import ActivityStreamPanel from "./components/ActivityStreamPanel";
import GoalProgressDateModal from "./components/ActivityStreamPanel/components/GoalProgress/components/GoalProgressDateModal";
import Checklist from "./components/Checklist";
import ArchiveGoalModal from "./components/Modals/ArchiveGoalModal";
import CompleteGoalModal from "./components/Modals/CompleteGoalModal";
import ShareGoalModal from "./components/Modals/ShareGoalModal";
import UpdateProgressModal from "./components/Modals/UpdateProgressModal";
import SingleGoalHeader from "./components/SingleGoalHeader";
import SingleGoalInfo from "./components/SingleGoalInfo";

import ErrorPage from "../../../pages/Error";

import {
	useCompanySettings,
	useCurrentUser,
	useDeleteGoal,
	useGoalDetails,
	useGoalHistory,
	useRbacObjectPermissions,
} from "../../../api/hooks";
import { useIsNewNavigation } from "../../../api/hooks/defs/navigation";
import { useEmployeeDashboardOrigin } from "../../../hooks/useEmployeeDashboardOrigin";
import useSavedPageState from "../../../hooks/usePageSavedPageState";
//hooks
import usePageWidthBreakpoint from "../../../hooks/usePageWidthBreakpoint";
import useSearchQueryParams from "../../../hooks/useSearchQueryParams";

//utils
import {
	ANALYTICS_LANDING_HREF,
	DASHBOARD_ORIGIN,
	isFreshTabLoad,
	parseReportOrigin,
} from "../../../utils/breadcrumbOrigin";
import { employeeDashboardRootCrumb } from "../../../utils/employeeDashboardBreadcrumbs";
import { parseGoalsOrigin } from "../../../utils/goalsBreadcrumbs";

//templates
import BasePageTemplate from "../../../pageTemplates/BasePageTemplate";
//css
import css from "./SingleGoal.module.scss";
//config
import Config from "./config";

const SingleGoal = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { isDesktop, isTablet, isMobile } = usePageWidthBreakpoint();
	const { goal_id: goalId } = useParams<{ goal_id: string }>();
	const { data: me } = useCurrentUser();
	const currentUserId = me?.me?.authorization?.payload?.user_id || "";
	const showBreadcrumbs = useIsNewNavigation();

	const {
		queryParams: { modal },
		handleQueryChange,
	} = useSearchQueryParams({
		schema: Config.queryParametersSchema,
	});

	const {
		pageState: { isActivityStreamClosed },
		updatePageState,
		isSavedPageStateLoading,
	} = useSavedPageState({
		pageName: "goal_progress",
		schema: Config.savedQueryParametersSchema,
	});

	const { isLoading: isCompanySettingsLoading } = useCompanySettings();

	const dashboardOrigin = useEmployeeDashboardOrigin({ deep: true });

	const {
		data,
		isLoading: isInitialGoalLoading,
		isError,
		error,
	} = useGoalDetails({ goalID: goalId || "", disableDefaultErrorHandling: true });

	const { isLoading: isInitialRbacLoading } = useRbacObjectPermissions("goal", goalId || "");
	const { isLoading: isGoalProgressHistoryInitialLoading } = useGoalHistory({
		goalID: goalId || "",
		type: "progress",
		sortDirection: "desc",
		sortName: "as_of",
		disableDefaultErrorHandling: true,
	});

	const { isLoading: isGoalHistoryInitialLoading } = useGoalHistory({
		goalID: goalId || "",
		sortDirection: "desc",
		sortName: "_id",
		disableDefaultErrorHandling: true,
	});

	const { mutate: deleteGoal, isPending: isDeleting } = useDeleteGoal({
		onSuccess: () => {
			// we need to determine history stack length, if length is 1, we need to navigate to the dashboard page, else go back
			const historylength = window?.history?.length;
			// -1 is a valid "to" value, but the TS compiler doesn't allow it
			const navigateTo: To = historylength > 1 ? (-1 as To) : "/";

			navigate(navigateTo);
		},
	});

	useEffect(() => {
		if (!isActivityStreamClosed) {
			if (isMobile || isTablet) {
				updatePageState({ isActivityStreamClosed: true }, { save: false });
			}
		}
	}, [isDesktop, isTablet]);

	const closeModal = () => handleQueryChange({ modal: undefined, editHistoryEntryId: undefined });
	const goal = data?.goal;

	if (isError) {
		return <ErrorPage code={(error as AxiosError)?.response?.status || 500} />;
	}

	if (
		!goal?._id ||
		isInitialGoalLoading ||
		isInitialRbacLoading ||
		isGoalProgressHistoryInitialLoading ||
		isGoalHistoryInitialLoading ||
		isCompanySettingsLoading ||
		isSavedPageStateLoading
	) {
		return <ComponentLoader />;
	}

	const breadcrumbItems = (() => {
		if (!showBreadcrumbs) return [];

		// Dashboard origins survive new-tab opens, so they outrank the fresh-tab Dashboard anchor.
		// Render nothing while the employee name resolves rather than flashing a generic root.
		if (dashboardOrigin.status === "loading") return [];
		if (dashboardOrigin.status === "resolved") {
			const goalsLeafLabel = dashboardOrigin.viaHref?.includes("/goals/teamGoals") ? "Team Goals" : "Goals";
			return [
				employeeDashboardRootCrumb(dashboardOrigin.from, dashboardOrigin.employeeName),
				{
					name: `${dashboardOrigin.employeeName}'s ${goalsLeafLabel}`,
					href: dashboardOrigin.viaHref ?? `/employees/${dashboardOrigin.employeeId}/goals/`,
					pageId: "breadcrumb-goals-parent",
				},
				{ name: goal?.name || "Goal", href: location.pathname, pageId: "breadcrumb-goal-current" },
			];
		}

		// Fresh tab: anchor on Dashboard.
		if (isFreshTabLoad()) {
			return [
				{ name: DASHBOARD_ORIGIN.label, href: DASHBOARD_ORIGIN.href, pageId: "breadcrumb-goals-parent" },
				{ name: goal?.name || "Goal", href: location.pathname, pageId: "breadcrumb-goal-current" },
			];
		}

		// Opened from a Reporting & Analytics report: root on "Reporting & Analytics > {Report Name}".
		const reportOrigin = parseReportOrigin(location.search);
		if (reportOrigin) {
			return [
				{
					name: "Reporting & Analytics",
					href: ANALYTICS_LANDING_HREF,
					pageId: "breadcrumb-analytics-parent",
					forceHardReload: true,
				},
				{
					name: reportOrigin.reportName,
					href: reportOrigin.reportHref,
					pageId: "breadcrumb-analytics-report",
					forceHardReload: true,
				},
				{ name: goal?.name || "Goal", href: location.pathname, pageId: "breadcrumb-goal-current" },
			];
		}

		const { from, label } = parseGoalsOrigin(location.search);
		if (label) {
			return [
				{ name: label, href: from, pageId: "breadcrumb-goals-parent" },
				{ name: goal?.name || "Goal", href: location.pathname, pageId: "breadcrumb-goal-current" },
			];
		}

		// No recognized origin (cross-context link or deep-link): root on the goal's owner.
		// Wait for currentUserId so we don't flash the wrong root before it resolves.
		if (!currentUserId) return [];
		const parent =
			goal?.assignee?._id === currentUserId
				? {
						name: "My Goals",
						href: `/employees/${currentUserId}/goals/mygoals/?filters=${encodeURIComponent('{"status":["active"]}')}`,
					}
				: { name: "Company Goals", href: "/company/goals/list?ownership=team&name=end_date&direction=desc" };
		return [
			{ ...parent, pageId: "breadcrumb-goals-parent" },
			{ name: goal?.name || "Goal", href: location.pathname, pageId: "breadcrumb-goal-current" },
		];
	})();

	return (
		<BasePageTemplate title={goal?.name || "Performyard Goal"}>
			<div className={css.singleGoalContainer}>
				<div className={css.mainContentContainer}>
					<Spacer height={2} />
					{breadcrumbItems.length > 0 && (
						<>
							<Breadcrumbs items={breadcrumbItems} />
							<Spacer height={2} />
						</>
					)}
					<SingleGoalHeader />
					<Spacer height={2} />
					<SingleGoalInfo />
					<Spacer height={2} />
					<Checklist />
					{isTablet || isMobile ? (
						<Button
							className={css.activityButton}
							onClick={() => {
								updatePageState({ isActivityStreamClosed: undefined }, { save: false });
							}}
						>
							Show Activity
						</Button>
					) : null}
				</div>
				<div className={css.slideoutContainer}>
					<ActivityStreamPanel />
				</div>
			</div>
			<UpdateProgressModal />
			<CompleteGoalModal />
			<ArchiveGoalModal />

			<ContentModal
				isOpen={modal === "delete"}
				handleClose={closeModal}
				title={
					<div className={css.modalContent}>
						<Typography size="sub-heading" weight="semi-bold">
							Delete Goal?
						</Typography>
						<Typography color="neutral-4" size="paragraph-2" weight="regular">
							{goal?.name}
						</Typography>
					</div>
				}
				buttons={[
					<Button key="cancel-delete-goal" testid="cancel-delete-goal" onClick={closeModal}>
						Cancel
					</Button>,
					<Button
						key="delete-goal"
						testid="delete-goal"
						color="green"
						isRequesting={isDeleting}
						onClick={() => {
							deleteGoal(goalId || "");
						}}
					>
						Yes, Delete Goal
					</Button>,
				]}
			>
				<Typography className={css.modalContent} size="paragraph-1" weight="semi-bold">
					Are you sure you want to delete this goal?
				</Typography>
			</ContentModal>
			<ShareGoalModal />
			<GoalProgressDateModal isOpen={modal === "edit-as-of-date"} handleClose={closeModal} />
		</BasePageTemplate>
	);
};

export default SingleGoal;
